/** Install-offer dismiss cadence for Admin PWA when opened in a browser tab. */

import type { AdminPwaInstallSurface } from './adminPwaSurfaces.js';

export const ADMIN_INSTALL_OFFER_DISMISSED_AT_KEY = 'admin-pwa-install-dismissed-at';
export const ADMIN_DEV_INSTALL_OFFER_DISMISSED_AT_KEY = 'admin-pwa-dev-install-dismissed-at';

/** Hide the install offer for this interval after Cancel (7 days). */
export const ADMIN_INSTALL_OFFER_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

export function adminInstallOfferDismissedAtKey(
  surface: AdminPwaInstallSurface = 'tenant',
): string {
  return surface === 'dev'
    ? ADMIN_DEV_INSTALL_OFFER_DISMISSED_AT_KEY
    : ADMIN_INSTALL_OFFER_DISMISSED_AT_KEY;
}

export function isAdminPwaInstalled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const standaloneDisplay =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone =
    'standalone' in navigator &&
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standaloneDisplay || iosStandalone;
}

export function readAdminInstallOfferDismissedAt(
  storage: Pick<Storage, 'getItem'> = window.localStorage,
  surface: AdminPwaInstallSurface = 'tenant',
): number | null {
  const raw = storage.getItem(adminInstallOfferDismissedAtKey(surface));
  if (raw === null || raw === '') {
    return null;
  }
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

/** True when the install banner may be shown (cooldown elapsed or never dismissed). */
export function isAdminInstallOfferDue(
  nowMs: number = Date.now(),
  storage: Pick<Storage, 'getItem'> = window.localStorage,
  surface: AdminPwaInstallSurface = 'tenant',
): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  if (isAdminPwaInstalled()) {
    return false;
  }
  const dismissedAt = readAdminInstallOfferDismissedAt(storage, surface);
  if (dismissedAt === null) {
    return true;
  }
  return nowMs - dismissedAt >= ADMIN_INSTALL_OFFER_COOLDOWN_MS;
}

export function markAdminInstallOfferDismissed(
  nowMs: number = Date.now(),
  storage: Pick<Storage, 'setItem'> = window.localStorage,
  surface: AdminPwaInstallSurface = 'tenant',
): void {
  storage.setItem(adminInstallOfferDismissedAtKey(surface), String(nowMs));
}

/**
 * Records that the install offer was shown so cooldown applies (customer mark-on-show semantics).
 * Same storage key as {@link markAdminInstallOfferDismissed}.
 */
export const markAdminInstallOfferShown = markAdminInstallOfferDismissed;

export function clearAdminInstallOfferDismissed(
  storage: Pick<Storage, 'removeItem'> = window.localStorage,
  surface: AdminPwaInstallSurface = 'tenant',
): void {
  storage.removeItem(adminInstallOfferDismissedAtKey(surface));
}
